import React, { useState } from "react";
import { BrowserProvider, Contract, solidityPackedKeccak256, isAddress } from "ethers";
import blockchainConfig from "../blockchain/config";
import abi from "../blockchain/ProvablyFairRNG.abi.json";

const InputPanel = ({ onCommitComplete, sessionId, secret }) => {
  const [rawAddresses, setRawAddresses] = useState("");
  const [sanitizedAddresses, setSanitizedAddresses] = useState([]);
  const [invalidAddresses, setInvalidAddresses] = useState([]);
  const [duplicatesRemoved, setDuplicatesRemoved] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Compute commitment using solidityPackedKeccak256
  const computeCommitment = (address, secret) => {
    return solidityPackedKeccak256(["address", "bytes32"], [address, secret]);
  };

  // Sanitize and validate addresses
  const sanitizeAddresses = () => {
    const addresses = rawAddresses
      .split(/[\s,]+/)
      .map((addr) => addr.trim())
      .filter((addr) => addr.length > 0);

    const valid = addresses.filter((addr) => isAddress(addr));
    const invalid = addresses.filter((addr) => !isAddress(addr));

    // Remove duplicates
    const uniqueValid = Array.from(new Set(valid));
    const removedCount = valid.length - uniqueValid.length;

    setSanitizedAddresses(uniqueValid);
    setInvalidAddresses(invalid);
    setDuplicatesRemoved(removedCount);

    setStatusMessage(
      `${uniqueValid.length} valid addresses sanitized. ${removedCount} duplicate(s) removed.`
    );
  };

  // Submit bulk commitment transaction
  const handleBulkCommit = async () => {
    try {
      if (!secret) {
        setStatusMessage("Please generate or enter a secret first.");
        return;
      }
      if (sanitizedAddresses.length === 0) {
        setStatusMessage("No valid addresses to add. Please sanitize the input first.");
        return;
      }
      if (!window.ethereum) {
        setStatusMessage("MetaMask is not installed.");
        return;
      }
      if (!sessionId) {
        setStatusMessage("Session ID is missing. Make sure a session has been started.");
        return;
      }

      setIsSubmitting(true);
      setStatusMessage("Submitting bulk commitments...");

      const commitmentsArray = sanitizedAddresses.map((address) =>
        computeCommitment(address, secret)
      );

      const provider = new BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const contract = new Contract(blockchainConfig.contractAddress, abi, signer);
      const tx = await contract.bulkCommit(sessionId, sanitizedAddresses, commitmentsArray);
      await tx.wait();

      setStatusMessage("Bulk commitments successfully submitted!");
      if (onCommitComplete) onCommitComplete(sanitizedAddresses);
    } catch (error) {
      console.error("Error in bulk commit:", error);
      setStatusMessage(`Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const copySanitizedAddresses = () => {
    navigator.clipboard.writeText(sanitizedAddresses.join(", "));
    setStatusMessage("Sanitized addresses copied to clipboard.");
  };

  return (
    <div className="p-6 bg-gray-900 rounded-lg shadow-lg">
      <h2 className="text-lg font-bold text-white">Submit Participants</h2>
      <h2 className="mt-6 text-lg font-bold text-white">Paste Raw Addresses</h2>
      <textarea
        value={rawAddresses}
        onChange={(e) => setRawAddresses(e.target.value)}
        placeholder="Paste raw addresses here (one per line or separated by spaces/commas)"
        className="w-full h-32 p-2 mt-2 text-white bg-gray-800 rounded"
      />
      <button
        onClick={sanitizeAddresses}
        className="px-4 py-2 mt-4 text-white bg-purple-500 rounded hover:bg-purple-700"
      >
        Sanitize Addresses
      </button>

      {sanitizedAddresses.length > 0 && (
        <div className="mt-4 text-white">
          <h3 className="text-lg font-bold">Sanitized Addresses:</h3>
          <textarea
            readOnly
            value={sanitizedAddresses.join(", ")}
            className="w-full h-32 p-2 mt-2 text-white bg-gray-800 rounded"
          />
          <h4 className="mt-2 text-sm font-medium">
            Duplicates Removed: {duplicatesRemoved}
          </h4>
          <button
            onClick={copySanitizedAddresses}
            className="px-4 py-2 mt-2 text-white bg-green-500 rounded hover:bg-green-700"
          >
            Copy Addresses
          </button>
        </div>
      )}

      {invalidAddresses.length > 0 && (
        <div className="mt-4 text-red-500">
          <h3 className="text-lg font-bold">Invalid Addresses:</h3>
          <ul className="list-disc list-inside">
            {invalidAddresses.map((addr, idx) => (
              <li key={idx}>{addr}</li>
            ))}
          </ul>
        </div>
      )}

      <button
        onClick={handleBulkCommit}
        className="px-4 py-2 mt-4 text-white bg-green-500 rounded hover:bg-green-700"
        disabled={isSubmitting}
      >
        Commit Participants
      </button>

      {statusMessage && <p className="mt-4 text-white">{statusMessage}</p>}
    </div>
  );
};

export default InputPanel;
